export const SHELL_ROUTE_PATTERNS = Object.freeze([
  '/login',
  '/oauth-callback',
  '/oauth-callback/login',
  '/unauthorized',
  '/',
  '/wiki',
  '/my-profile',
  '/favorites',
  '/notifications',
  '/support/tickets',
  '/calendar',
  '/backup',
  '/activity-feed',
  '/statistics',
  '/statistics/dashboard',
  '/statistics/managers-talents',
  '/statistics/managers-videos',
  '/statistics/managers-youtube',
  '/statistics/managers-hired',
  '/statistics/managers-started',
  '/statistics/custom-reports',
  '/statistics/period-comparison',
  '/statistics/period-comparison-videos',
  '/statistics/period-comparison-youtube',
  '/statistics/managers-portrait',
  '/statistics/period-comparison-portrait',
  '/statistics/period-comparison-hired',
  '/statistics/period-comparison-started',
  '/statistics/onboarding',
  '/statistics/hr-recruitment',
  '/talents',
  '/talents/new',
  '/talents/:id',
  '/talents/:id/edit',
  '/candidates',
  '/candidates/rejected',
  '/candidates/:stageKey',
  '/trainees',
  '/employees',
  '/employees-active',
  '/employees-project',
  '/employees-all',
  '/presale',
  '/presale/history',
  '/statuses',
  '/statuses/order-management',
  '/stages',
  '/job-applications',
  '/job-applications/new',
  '/job-applications/:id',
  '/job-applications/:id/edit',
  '/talent-templates',
  '/talent-templates/:id',
  '/talent-posts',
  '/talent-posts/:id',
  '/prompts',
  '/prompts/:id',
  '/notification-settings',
  '/playbook-settings',
  '/stages-dashboard',
]);

const talentPermission = (resource, action) => ['talent-service', resource, action];

export const STATIC_TALENT_NAVIGATION = Object.freeze([
  { id: 'dashboard', label: 'Dashboard', path: '/', icon: 'Home' },
  {
    id: 'people',
    label: 'People',
    icon: 'Users',
    variant: 'section',
    anyPermissions: [
      talentPermission('talent', 'list'),
      talentPermission('candidate', 'list'),
      talentPermission('trainee', 'list'),
      talentPermission('employee', 'list'),
      talentPermission('presale', 'list'),
    ],
    children: [
      { id: 'talents', label: 'Talents', path: '/talents', icon: 'Users', permission: talentPermission('talent', 'list') },
      { id: 'favorites', label: 'Favorites', path: '/favorites', icon: 'Star' },
      {
        id: 'candidates',
        label: 'Candidates',
        icon: 'GraduationCap',
        permission: talentPermission('candidate', 'list'),
        candidateStagesSlot: true,
        children: [
          { id: 'candidates-all', label: 'Candidates', path: '/candidates', icon: 'GraduationCap' },
          { id: 'candidates-rejected', label: 'Rejected', path: '/candidates/rejected', icon: 'XCircle' },
        ],
      },
      { id: 'trainees', label: 'Trainees', path: '/trainees', icon: 'BookOpen', permission: talentPermission('trainee', 'list') },
      {
        id: 'employees',
        label: 'Employees',
        icon: 'Briefcase',
        permission: talentPermission('employee', 'list'),
        children: [
          { id: 'employees-default', label: 'Employees', path: '/employees', icon: 'Briefcase' },
          { id: 'employees-active', label: 'Active Employees', path: '/employees-active', icon: 'Briefcase' },
          { id: 'employees-project', label: 'Project Employees', path: '/employees-project', icon: 'Briefcase' },
          { id: 'employees-all', label: 'All Employees', path: '/employees-all', icon: 'Users', permission: talentPermission('employee', 'list-all') },
        ],
      },
      {
        id: 'presale',
        label: 'Presale',
        icon: 'Target',
        permission: talentPermission('presale', 'list'),
        children: [
          { id: 'presale-default', label: 'Presale', path: '/presale', icon: 'Target' },
          { id: 'presale-history', label: 'Presale History', path: '/presale/history', icon: 'History' },
        ],
      },
    ],
  },
  {
    id: 'recruitment',
    label: 'Recruitment',
    icon: 'Briefcase',
    variant: 'section',
    anyPermissions: [
      talentPermission('job_application', 'list'),
      talentPermission('candidate', 'list'),
      talentPermission('playbook_instance', 'list'),
    ],
    children: [
      { id: 'job-applications', label: 'Job Applications', path: '/job-applications', icon: 'FileText', permission: talentPermission('job_application', 'list') },
      {
        id: 'talent-calendar',
        label: 'Talent Calendar',
        path: '/calendar',
        icon: 'Calendar',
        anyPermissions: [
          talentPermission('candidate', 'list'),
          talentPermission('trainee', 'list'),
          talentPermission('employee', 'list'),
          talentPermission('presale', 'list'),
        ],
      },
      { id: 'active-stages', label: 'Active Stages', path: '/stages-dashboard', icon: 'Activity', permission: talentPermission('playbook_instance', 'list') },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'BarChart3',
    variant: 'section',
    anyPermissions: [talentPermission('statistics', 'read'), talentPermission('statistics', 'read-all')],
    children: [
      { id: 'statistics-dashboard', label: 'Dashboard', path: '/statistics/dashboard', icon: 'LayoutDashboard' },
      { id: 'statistics-hr-funnel', label: 'HR Funnel', path: '/statistics/hr-recruitment', icon: 'BarChart3' },
      {
        id: 'statistics-recruitment',
        label: 'Recruitment',
        children: [
          {
            id: 'statistics-created',
            label: 'Created',
            children: [
              { id: 'statistics-managers-talents', label: 'Added by Managers', path: '/statistics/managers-talents', icon: 'Users' },
              { id: 'statistics-custom-reports', label: 'Custom Reports', path: '/statistics/custom-reports', icon: 'BarChart3' },
              { id: 'statistics-period-comparison', label: 'Period Comparison', path: '/statistics/period-comparison', icon: 'TrendingUp' },
            ],
          },
          {
            id: 'statistics-video',
            label: 'Video',
            children: [
              { id: 'statistics-managers-videos', label: 'Added Video', path: '/statistics/managers-videos', icon: 'Video' },
              { id: 'statistics-period-comparison-videos', label: 'Period Comparison', path: '/statistics/period-comparison-videos', icon: 'TrendingUp' },
            ],
          },
          {
            id: 'statistics-hired',
            label: 'Hired',
            children: [
              { id: 'statistics-managers-hired', label: 'Hired by Managers', path: '/statistics/managers-hired', icon: 'UserCheck' },
              { id: 'statistics-period-comparison-hired', label: 'Period Comparison', path: '/statistics/period-comparison-hired', icon: 'TrendingUp' },
            ],
          },
          {
            id: 'statistics-start',
            label: 'Start',
            children: [
              { id: 'statistics-managers-started', label: 'Start by Managers', path: '/statistics/managers-started', icon: 'PlayCircle' },
              { id: 'statistics-period-comparison-started', label: 'Period Comparison', path: '/statistics/period-comparison-started', icon: 'TrendingUp' },
            ],
          },
          {
            id: 'statistics-onboarding-group',
            label: 'Onboarding',
            children: [
              { id: 'statistics-onboarding', label: 'Onboarding Dashboard', path: '/statistics/onboarding', icon: 'UserPlus' },
            ],
          },
        ],
      },
      {
        id: 'statistics-video-editors',
        label: 'Video Editors',
        children: [
          { id: 'statistics-managers-youtube', label: 'YouTube & Shorts', path: '/statistics/managers-youtube', icon: 'Youtube' },
          { id: 'statistics-period-comparison-youtube', label: 'Period Comparison', path: '/statistics/period-comparison-youtube', icon: 'TrendingUp' },
        ],
      },
      {
        id: 'statistics-designers',
        label: 'Designers',
        children: [
          { id: 'statistics-managers-portrait', label: 'Portraits by Managers', path: '/statistics/managers-portrait', icon: 'Image' },
          { id: 'statistics-period-comparison-portrait', label: 'Period Comparison', path: '/statistics/period-comparison-portrait', icon: 'TrendingUp' },
        ],
      },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    icon: 'Send',
    variant: 'section',
    children: [
      {
        id: 'talent-posting',
        label: 'Talent Posting',
        icon: 'Send',
        anyPermissions: [
          talentPermission('talent_template', 'list'),
          talentPermission('talent_post', 'list'),
          talentPermission('prompt_template', 'list'),
        ],
        children: [
          { id: 'talent-templates', label: 'Templates', path: '/talent-templates', icon: 'FileText', permission: talentPermission('talent_template', 'list') },
          { id: 'talent-posts', label: 'Posts', path: '/talent-posts', icon: 'Send', permission: talentPermission('talent_post', 'list') },
          { id: 'prompts', label: 'Prompts', path: '/prompts', icon: 'Bot', permission: talentPermission('prompt_template', 'list') },
        ],
      },
      { id: 'wiki', label: 'Wiki', path: '/wiki', icon: 'BookOpen' },
      { id: 'notifications', label: 'Notifications', path: '/notifications', icon: 'Inbox' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: 'Settings',
    variant: 'section',
    anyPermissions: [
      talentPermission('playbook', 'list'),
      talentPermission('status', 'read'),
      talentPermission('status', 'list'),
      talentPermission('notification_config', 'list'),
      talentPermission('backup', 'read'),
      talentPermission('events', 'list'),
      ['support-service', 'ticket', 'list'],
    ],
    children: [
      { id: 'activity-feed', label: 'Activity Feed', path: '/activity-feed', icon: 'Activity', permission: talentPermission('events', 'list') },
      { id: 'support-tickets', label: 'Support Tickets', path: '/support/tickets', icon: 'LifeBuoy', permission: ['support-service', 'ticket', 'list'] },
      { id: 'playbooks', label: 'Playbooks', path: '/playbook-settings', icon: 'GitBranch', permission: talentPermission('playbook', 'list') },
      { id: 'statuses', label: 'Statuses', path: '/statuses', icon: 'Tag', anyPermissions: [talentPermission('status', 'read'), talentPermission('status', 'list')] },
      { id: 'stages', label: 'Stages', path: '/stages', icon: 'Layers', anyPermissions: [talentPermission('stage', 'read'), talentPermission('stage', 'list')] },
      { id: 'notification-settings', label: 'Discord Notifications', path: '/notification-settings', icon: 'Bell', permission: talentPermission('notification_config', 'list') },
      { id: 'database-backup', label: 'Database Backup', path: '/backup', icon: 'Database', permission: talentPermission('backup', 'read') },
    ],
  },
  { id: 'my-profile', label: 'My Profile', path: '/my-profile', icon: 'UserCircle' },
]);

export { TALENT_DETAIL_SECTIONS } from './talentDetailNavigationConfig.js';
